// Cookie consent storage + helpers.
//
// Stores the visitor's GDPR cookie-preference choices so the banner is shown
// only on first visit (or when the consent policy version changes).
//
// === Why localStorage, not sessionStorage or HttpOnly cookies? ===
// 1. **localStorage** is the correct primitive here because GDPR consent
//    decisions MUST persist across browser sessions — that is the entire point
//    of recording a user's choice (sessionStorage would force a re-prompt on
//    every visit, which is itself a regulatory violation of "respect prior
//    consent").
// 2. The stored payload contains NO secrets, NO PII, and NO authentication
//    material — only the visitor's category toggles (analytics: true/false,
//    marketing: true/false) plus a version + timestamp. There is nothing here
//    that an XSS payload could exfiltrate that would be of value.
// 3. Admin authentication uses an HttpOnly cookie (see /app/backend/auth.py).
//    That is the correct primitive for actual secrets. We deliberately keep
//    those two concerns separate.
//
// Failure modes:
//   * Storage may be disabled (Safari private mode, embedded WebView, quota).
//     We log a single warn so users can diagnose, but never throw — consent
//     defaults to "not yet decided" which re-shows the banner gracefully.

import { log } from "@/lib/logger";

export const CONSENT_VERSION = "v1";
export const CONSENT_STORAGE_KEY = "seetai_cookie_consent";
export const CONSENT_EVENT = "seetai:cookie-consent-changed";

export const DEFAULT_CONSENT = {
  version: CONSENT_VERSION,
  essential: true, // always true; cannot be disabled
  analytics: false,
  marketing: false,
  decidedAt: null,
};

export const getConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Force re-prompt if policy version changed
    if (parsed?.version !== CONSENT_VERSION) return null;
    return { ...DEFAULT_CONSENT, ...parsed, essential: true };
  } catch (err) {
    // Storage unavailable or JSON corruption. Treat as "no decision yet" so
    // the banner re-prompts the user. Log so we can spot recurring issues.
    log.warn("[cookieConsent.getConsent] failed to read consent payload:", err);
    return null;
  }
};

export const setConsent = (consent) => {
  const payload = {
    ...DEFAULT_CONSENT,
    ...consent,
    version: CONSENT_VERSION,
    essential: true,
    decidedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(payload));
    // Broadcast for in-page listeners (e.g. analytics loaders)
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: payload }));
  } catch (err) {
    // Storage may be disabled (private mode, quota, embedded WebView). The
    // choice still lives in memory for the current page, but won't survive
    // a reload — surface this so we know if it's happening in the wild.
    log.warn("[cookieConsent.setConsent] could not persist consent:", err);
  }
  return payload;
};

export const clearConsent = () => {
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: null }));
  } catch (err) {
    log.warn("[cookieConsent.clearConsent] could not clear consent:", err);
  }
};

// Helper: open the banner programmatically (e.g. from Footer 'Cookie Preferences')
export const COOKIE_BANNER_OPEN_EVENT = "seetai:cookie-banner-open";
export const openCookieBanner = () => {
  window.dispatchEvent(new CustomEvent(COOKIE_BANNER_OPEN_EVENT));
};
